const DEBUG_DISCOUNTS = true;
const debugLog = (ns) => (...args) => DEBUG_DISCOUNTS && console.log(`[discounts:${ns}]`, ...args);

const CartDiscountUtil = {
  async apply(code) {
    const log = debugLog('apply');
    try {
      log('applying code', code);
      await fetch(`/discount/${code}?v=${Date.now()}`);
      localStorage.setItem('discountCode', JSON.stringify({ code }));
      log('applied', code);
    } catch (err) {
      console.error('Discount error:', err);
    }
  },

  clear() {
    const log = debugLog('clear');
    log('clearing discount');
    localStorage.removeItem('discountCode');
    fetch('/checkout?discount=%20');
    fetch('/discount/CLEAR');
  },

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  },

  async addFreeGifts(cart) {
    const log = debugLog('freeGifts');
    const FREE_GIFT_VARIANTS = [55129774915908, 56615228408132, 56615242858820];
    const inCart = new Set(cart.items.map(i => i.variant_id));
    const toAdd = FREE_GIFT_VARIANTS.filter(id => !inCart.has(id));

    if (toAdd.length === 0) {
      log('all free gift variants already in cart');
      return;
    }

    // Ask the theme which sections need re-rendering (cart drawer registers itself here)
    const sections = [];
    document.dispatchEvent(new CustomEvent('cart:push:section.id', { bubbles: true, detail: { sections } }));
    log('sections to re-render', sections);

    log('adding free gift variants', toAdd);
    const added = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        items: toAdd.map(id => ({ id, quantity: 1 })),
        ...(sections.length && { sections })
      })
    }).then(r => r.json());

    log('fetching updated cart');
    const updatedCart = await fetch('/cart.js').then(r => r.json());
    updatedCart.sections = added.sections ?? {};
    log('sections received from add response', Object.keys(updatedCart.sections));

    log('dispatching cart:change to reload cart drawer');
    document.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'variant:add',
        items: added.items ?? [added],
        cart: updatedCart
      }
    }));
    log('cart:change dispatched');
  },

  async checkAndApplyTrustpilot() {
    if (this._applying) return;
    this._applying = true;
    const log = debugLog('trustpilot');
    try {
      log('checking cart for fatbike items');
      const [cart, { products }] = await Promise.all([
        fetch('/cart.js').then(r => r.json()),
        fetch('/collections/fatbikes/products.json?limit=250').then(r => r.json())
      ]);

      const fatbikeIds = new Set(products.map(p => p.id));
      const match = cart.items.find(item => fatbikeIds.has(item.product_id));
      log(match ? `qualifying item found: ${match.title}` : 'no qualifying items in cart');

      if (match) {
        await this.addFreeGifts(cart);
        await this.apply('TRUSTPILOT');
      }
    } catch (err) {
      console.error('Trustpilot discount check error:', err);
    } finally {
      this._applying = false;
    }
  }
};

const logInit = debugLog('init');

// Store UTM flag for the duration of the session
if (new URLSearchParams(window.location.search).get('utm_campaign') === 'c41ff6') {
  logInit('trustpilot UTM detected, storing session flag');
  sessionStorage.setItem('tp_utm', '1');
}

// Everything below is exclusive to Trustpilot UTM visitors
if (sessionStorage.getItem('tp_utm')) {
  logInit('trustpilot session active');

  // Restore saved discount from a previous page in this session
  const saved = localStorage.getItem('discountCode');
  if (saved) {
    logInit('restoring saved discount', JSON.parse(saved).code);
    CartDiscountUtil.apply(JSON.parse(saved).code);
  } else {
    const cookieCode = CartDiscountUtil.getCookie('discount_code');
    if (cookieCode) {
      logInit('restoring cookie discount', cookieCode);
      CartDiscountUtil.apply(cookieCode);
    }
  }

  // Apply TRUSTPILOT + add free gifts whenever a fatbike is in cart
  CartDiscountUtil.checkAndApplyTrustpilot();
  document.addEventListener('cart:change', () => {
    logInit('cart:change fired, re-checking');
    CartDiscountUtil.checkAndApplyTrustpilot();
  });
}

async function showTrustpilotPopup() {
  if (sessionStorage.getItem('tp_popup_dismissed')) return;

  const log = debugLog('popup');
  log('fetching trustpilot-popup section');

  const sections = await fetch('/?sections=trustpilot-popup').then(r => r.json()).catch(err => {
    console.error('Failed to fetch popup section:', err);
    return null;
  });
  if (!sections) return;

  const html = sections['trustpilot-popup'];
  log('section HTML length', html?.length ?? 0);
  if (!html) { console.error('[discounts:popup] section returned empty'); return; }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  const sectionEl = wrapper.firstElementChild;
  if (!sectionEl) { console.error('[discounts:popup] could not parse section element'); return; }
  document.body.appendChild(sectionEl);
  log('popup shown');

  const close = () => {
    log('popup dismissed');
    sessionStorage.setItem('tp_popup_dismissed', '1');
    sectionEl.style.transition = 'opacity 0.2s ease';
    sectionEl.style.opacity = '0';
    setTimeout(() => sectionEl.remove(), 200);
  };

  sectionEl.querySelector('#tp-deal-close').addEventListener('click', close);
  sectionEl.querySelector('#tp-deal-popup').addEventListener('click', e => {
    if (e.target === e.currentTarget) close();
  });
}

// Show popup on the UTM landing page
if (new URLSearchParams(window.location.search).get('utm_campaign') === 'c41ff6') {
  showTrustpilotPopup();
}

// Dev helper: open the popup from the console: window.__tpPopup()
window.__tpPopup = () => {
  sessionStorage.setItem('tp_utm', '1');
  sessionStorage.removeItem('tp_popup_dismissed');
  showTrustpilotPopup();
};
